import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import { useTheme } from "@mui/material/styles";
import {
  MY_LEAVE_DETAILS_BY_CONTRACT,
  MY_LEAVE_LEAVES_SUBSCRIPTION,
} from "my-leave/graphql";
import { OcChart } from "oc/components";
import { useOcCurrentUserState, useOcPolling } from "oc/hooks";
import { formatLeaveDays } from "my-leave/utils/format-leave-days";

interface MyLeaveDetailsChartProps {
  selectedYear: number;
}

export const MyLeaveDetailsChart: React.FC<MyLeaveDetailsChartProps> = ({
  selectedYear,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  let { contractCode } = useOcCurrentUserState();

  const colorPalette: { [key: string]: string } = {
    baseLeaveDays: theme.chartColors.denimBlue,
    onAge: theme.chartColors.lightOrange,
    onCountOfChildren: theme.chartColors.camellia,
    onStudy: theme.chartColors.turquoise,
    onPreviousYear: theme.chartColors.pollen,
    onCovidPlus10: theme.chartColors.lavenderGray,
    onExposedToRadiationState: theme.chartColors.mint,
    onDoubleRiskState: theme.chartColors.lilac,
    onBlindnessSate: theme.chartColors.pebble,
    onJobClass: theme.chartColors.stormGray,
    onJobGrade: theme.chartColors.cream,
    onManagerState: theme.chartColors.cerulian,
    onPensionerState: theme.chartColors.mistBlue,
    onTrainerState: theme.chartColors.flamingo,
    onYoungAge: theme.chartColors.darkOlive,
    other: theme.chartColors.grapefruit,
    onESZJTV19: theme.chartColors.roseGold,
    onJobYears: theme.chartColors.flash,
    onPreviousYearBaseLeaves: theme.chartColors.redRose,
    onAge50Years: theme.chartColors.peach,
    onPreviousYearAdditionalLeaves: theme.chartColors.camel,
    onEmergency: theme.chartColors.papaya,
  };

  const [loaded, setLoaded] = React.useState(false);

  const {
    data: LeaveDetails,
    loading: LeaveDetailsLoading,
    error: LeaveDetailsError,
    refetch,
  } = useQuery(MY_LEAVE_DETAILS_BY_CONTRACT, {
    skip: !contractCode || !selectedYear,
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: { contract: contractCode, year: selectedYear },
    notifyOnNetworkStatusChange: true,
  });

  // Handle loading state (replaces onCompleted)
  React.useEffect(() => {
    if (LeaveDetails && !LeaveDetailsLoading) {
      setLoaded(true);
    }
  }, [LeaveDetails, LeaveDetailsLoading]);

  // Handle error state (replaces onError)
  React.useEffect(() => {
    if (LeaveDetailsError) {
      console.log("err", LeaveDetailsError);
    }
  }, [LeaveDetailsError]);

  useSubscription(MY_LEAVE_LEAVES_SUBSCRIPTION, {
    fetchPolicy: "network-only",
    skip: !contractCode || !selectedYear,
    onData: () => {
      refetch();
    },
  });

  let myFulfilledLeaveDetails = LeaveDetails?.leaveDetailsByContract || [];
  /** Result */
  let chartData = [];

  /**
   * Contruct result array
   * Filter only non zero values
   */
  if (myFulfilledLeaveDetails) {
    let resultData = myFulfilledLeaveDetails;

    /** Fill up result array */
    for (const property in resultData) {
      if (resultData[property] != null && resultData[property] !== 0) {
        let item = {
          label: t(`MY_LEAVE_${property.toUpperCase()}`),
          value: resultData[property],
          valueInLegend: formatLeaveDays(resultData[property]),
          color: colorPalette[property],
        };
        if (
          property !== "_id" &&
          property !== "__typename" &&
          property !== "proportionalLeaveDays" &&
          property !== "availableLeaveDays" &&
          property !== "availableStudyLeaveDays" &&
          property !== "availableESZJTV19LeaveDays" &&
          property !== "availableCovidPlus10LeaveDays" &&
          property !== "availableEmergencyLeaveDays" &&
          property !== "fulfilledLeaveDays" &&
          property !== "fulfilledStudyDays" &&
          property !== "fulfilledAbsenceDays" &&
          property !== "fulfilledESZJTV19Days" &&
          property !== "fulfilledCovidPlus10Days" &&
          property !== "fulfilledEmergencyDays"
        ) {
          chartData.push(item);
        }
      }
    }

    const allAvailableLeaveDays =
      myFulfilledLeaveDetails?.availableLeaveDays +
      myFulfilledLeaveDetails?.availableStudyLeaveDays +
      myFulfilledLeaveDetails?.availableESZJTV19LeaveDays +
      myFulfilledLeaveDetails?.availableCovidPlus10LeaveDays +
      myFulfilledLeaveDetails?.availableEmergencyLeaveDays +
      myFulfilledLeaveDetails?.fulfilledLeaveDays +
      myFulfilledLeaveDetails?.fulfilledStudyDays +
      myFulfilledLeaveDetails?.fulfilledESZJTV19Days +
      myFulfilledLeaveDetails?.fulfilledCovidPlus10Days +
      myFulfilledLeaveDetails?.fulfilledEmergencyDays +
      myFulfilledLeaveDetails?.onPreviousYearBaseLeaves +
      myFulfilledLeaveDetails?.onPreviousYearAdditionalLeaves;

    return (
      <OcChart
        data={chartData}
        summaryTitle={
          Number.isNaN(allAvailableLeaveDays)
            ? "0"
            : formatLeaveDays(allAvailableLeaveDays).replace(".", ",")
        }
        summaryText={`${t("OC_SUMMARY")} ${t("MY_LEAVE_NEW_LEAVE_BASE")}`}
        loading={loaded ? false : LeaveDetailsLoading}
        variant="MyLeave"
      />
    );
  }
  return null;
};

export default MyLeaveDetailsChart;
